import React, { useContext, useState } from 'react';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import HttpClient from '../../../../core/HttpClient';
import { message } from 'antd';
import CompanyContext from '../../../contexts/company/CompanyContext';
import { EDegradedSubscriptionStatus, isExternallyBilled } from '@leedpulse-monorepo/enums';

import styles from './CompanySubscriptionsStatusAlert.module.css';

const CompanySubscriptionsStatusAlert: React.FC = () => {
  const { degradedSubscriptions, authentication } = useContext(AuthenticationContext);
  const { activeCompany } = useContext(CompanyContext);

  const [isDismissed, setDismissed] = useState(false); // User can hide the alert (will re appear on the next refresh)
  const [isSending, setIsSending] = useState(false);

  /**
   * The alert is dismissed by the user 
   * OR
   * The alert is not enabled (the company is all right)
   * OR
   * The company is externally billed (billing is managed by the partner, not by us)
   */
  if (degradedSubscriptions.length === 0 || isDismissed || isExternallyBilled(activeCompany?.partner_system, activeCompany?.id_stripe)) return null;

  const cancelledCount = degradedSubscriptions.filter((s) => s.status === 'cancelled').length;
  const pausedCount = degradedSubscriptions.filter((s) => s.status === 'paused').length;
  const total = degradedSubscriptions.length;
  const plural = total > 1;

  // Highest severity drives the variant (cancelled > paused).
  const variant: 'cancelled' | 'paused' = cancelledCount > 0 ? 'cancelled' : 'paused';

  const title = (() => {
    if (cancelledCount > 0 && pausedCount > 0) return plural ? 'Abonnements résiliés / suspendus' : 'Abonnement résilié / suspendu';
    if (cancelledCount > 0) return plural ? `${cancelledCount} abonnements résiliés` : 'Abonnement résilié';
    return plural ? `${pausedCount} abonnements suspendus` : 'Abonnement suspendu';
  })();

  const text = (() => {
    if (variant === 'cancelled') {
      return plural
        ? `${total} de vos numéros ont été résiliés. Les fonctionnalités associées sont limitées. Contactez votre gestionnaire pour réactiver vos numéros.`
        : 'Un de vos numéros a été résilié. Les fonctionnalités sont limitées. Contactez votre gestionnaire pour réactiver votre compte.';
    }
    return plural
      ? `${total} de vos numéros sont actuellement suspendus. Certaines fonctionnalités sont limitées. Contactez votre gestionnaire pour plus d'informations.`
      : "Un de vos numéros est actuellement suspendu. Certaines fonctionnalités sont limitées. Contactez votre gestionnaire pour plus d'informations.";
  })();

  const ctaLabel = variant === 'paused'
    ? 'Contacter le gestionnaire'
    : (plural ? 'Réactiver mes numéros' : 'Réactiver mon numéro');

  const handleCtaClick = async () => {
    if (!authentication?.token) {
      message.error("Vous devez être connecté pour effectuer cette action.");
      return;
    }

    try {
      setIsSending(true);
      // Send per-subscription degraded list so the backend can notify the team in Slack.
      await HttpClient.post(
        '/leedflow/degraded-mode/reactivation-request',
        { degraded_subscriptions: degradedSubscriptions },
        authentication.token,
        undefined,
        { 'x-api-key': activeCompany?.api_key ?? 'unknown' }
      );
      message.success("Demande envoyée. Nous revenons vers vous rapidement.");
    } catch (e) {
      console.error('degraded mode CTA error:', e);
      message.error("Impossible d'envoyer la demande pour le moment.");
    } finally {
      setIsSending(false);
    }
  };

  // J'ai pas réussi a appliquer correctement le style de l'alert Antd et du bouton pour correspondre à la maquette, du coup ça sera un composant unique en html / css pur
  return (
    <div
      className={[
        styles.banner,
        variant === EDegradedSubscriptionStatus.PAUSED ? styles.warning : styles.error,
      ].join(' ')}
      role={variant === EDegradedSubscriptionStatus.PAUSED ? 'status' : 'alert'}
      aria-live={variant === EDegradedSubscriptionStatus.PAUSED ? 'polite' : undefined}
    >
      <div className={styles.icon} aria-hidden="true">
        !
      </div>
      <div className={styles.body}>
        <p className={styles.title}>{title}</p>
        <p className={styles.text}>{text}</p>
      </div>
      <div className={styles.actions}>
        <button
          type="button"
          className={[styles.link, styles.solid].join(' ')}
          onClick={handleCtaClick}
          disabled={isSending}
        >
          {ctaLabel}
        </button>
        <button
          type="button"
          className={styles.close}
          aria-label="Fermer"
          onClick={() => setDismissed(true)}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path
              d="M1 1l12 12M13 1L1 13"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default CompanySubscriptionsStatusAlert;

